import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useVendor } from '../contexts/VendorContext';
import { Button } from './ui/button';
import {
  Store, LayoutDashboard, Package, ShoppingCart, CreditCard,
  BarChart3, Settings, LogOut, Menu, X, ChevronDown, ChevronRight,
  Bell, HelpCircle, Warehouse, Tag, Upload, Image as ImageIcon,
  AlertTriangle, TrendingUp, Home, RotateCcw, Layers
} from 'lucide-react';

export default function VendorLayout({ children }) {
  const { vendor, logout } = useVendor();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/vendor/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const menuItems = [
    { 
      label: 'Home', 
      icon: LayoutDashboard, 
      path: '/vendor/dashboard',
      badge: null
    },
    { 
      label: 'Orders', 
      icon: ShoppingCart, 
      path: '/vendor/orders',
      badge: null
    },
    { 
      label: 'Returns', 
      icon: RotateCcw, 
      path: '/vendor/returns',
      badge: null
    },
    { 
      label: 'Products', 
      icon: Package, 
      path: '/vendor/products',
      badge: null
    },
    { 
      label: 'Inventory', 
      icon: Warehouse, 
      path: '/vendor/inventory',
      badge: null
    },
    { 
      label: 'Bulk Operations', 
      icon: Layers, 
      path: '/vendor/bulk-operations',
      badge: null
    },
    { 
      label: 'Payments', 
      icon: CreditCard, 
      path: '/vendor/payments',
      badge: null
    },
    { 
      label: 'Analytics', 
      icon: BarChart3, 
      path: '/vendor/analytics',
      badge: null
    },
    { 
      label: 'Store Settings', 
      icon: Store, 
      path: '/vendor/store-settings',
      badge: null
    },
    { 
      label: 'Settings', 
      icon: Settings, 
      path: '/vendor/settings',
      badge: null
    }
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Store className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {vendor?.business_name || 'Vendor'}
            </p>
            <p className="text-xs text-gray-400 truncate">nammahut Seller</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                active
                  ? 'bg-green-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {item.badge && (
                <span className="ml-auto px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-700">
        <Link
          to="/"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-all mb-2"
        >
          <Home className="w-5 h-5" />
          <span className="font-medium">Visit Store</span>
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-red-600/20 hover:text-red-400 transition-all"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 bg-gray-900 transition-all duration-300 hidden lg:block ${
        sidebarOpen ? 'w-64' : 'w-20'
      }`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-64 bg-gray-900 z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:pl-64' : 'lg:pl-20'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-6 h-6" />
              </button>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:block p-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                Welcome back, {vendor?.owner_name?.split(' ')[0] || 'Seller'}!
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button className="p-2 rounded-lg hover:bg-gray-100 relative">
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>
              <button className="p-2 rounded-lg hover:bg-gray-100">
                <HelpCircle className="w-5 h-5 text-gray-600" />
              </button>
              <div className="hidden sm:flex items-center gap-2 pl-3 border-l">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {vendor?.owner_name?.charAt(0)?.toUpperCase() || 'V'}
                  </span>
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-900">{vendor?.business_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{vendor?.status}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
